import {
  IPermitTypeField,
  ITypeFieldFilterOptions,
  IWorkflowStep,
  OperatorType,
} from '~/client/graph'
import { locationFilterPropMap } from '~/client/src/shared/constants/FilterPropertyMaps/LocationFilterPropMap'
import { getResourceTypeIdForFieldType } from '~/client/src/shared/utils/permitFieldToResourceType'

/** One field of one workflow type, and whether this record should be allowed by it. */
export interface IAllowanceEdit {
  typeId: string
  fieldId: string
  shouldAllow: boolean
}

interface IAllowanceRow {
  id: string
  typeId: string
  fieldId: string
}

/** The rows whose ticked state differs between what was loaded and what the user left. */
export function diffAllowances(
  rows: IAllowanceRow[],
  baseline: Set<string>,
  draft: Set<string>,
): IAllowanceEdit[] {
  return rows
    .filter(row => baseline.has(row.id) !== draft.has(row.id))
    .map(row => ({
      typeId: row.typeId,
      fieldId: row.fieldId,
      shouldAllow: draft.has(row.id),
    }))
}

const isStaticIdFilter = (option: ITypeFieldFilterOptions): boolean =>
  option?.propertyPath === locationFilterPropMap.ID && !option?.fieldId

/**
 * The only shape a static id filter is ever written in: three fields, nothing else.
 *
 * `fieldId` and `fieldValues` belong exclusively to a conditional filter - carrying either
 * one here would turn this entry into a dependency rule that every static reader then skips.
 * Built fresh rather than spread from the entry being replaced, so nothing rides along: not a
 * stale `fieldValues`, and not the `__typename` a filter read back over GraphQL arrives with.
 * Matches what the configurator writes (ResourceRestriction).
 */
const staticIdFilter = (
  operator: OperatorType,
  values: Set<string>,
): ITypeFieldFilterOptions => ({
  propertyPath: locationFilterPropMap.ID,
  operator,
  values: Array.from(values),
})

/**
 * One field's `filterOptions` with this record allowed or denied, as a complete replacement
 * array - or the caller's own value, unchanged and by reference, when the record's answer
 * for this field does not move. Pure; the caller decides what to do with it.
 *
 * The field's static id filters are an allow-list (In) and a deny-list (NotIn) on
 * propertyPath `id`, and an entry with no values is the same as no entry - empty means
 * unrestricted, so an emptied entry has to be DROPPED rather than persisted. Dynamic filters
 * (those carrying a fieldId, which restrict conditionally on another field's selections) and
 * filters on any other property are carried through untouched, in their original order.
 *
 * Two rules earn their keep:
 *
 * A DENY always writes a NotIn entry, even when the record is already absent from a non-empty
 * allow-list. Absence is not denial: on a location field with LBS logic on, In is evaluated as
 * an OR-override rather than a restriction (`evaluateFiltersWithLbsContext`), so a record left
 * out of it is still allowed whenever LBS does not hide it. Absence is also not durable - both
 * `pruneMissingIdAllowances` and the configurator's own "everything selected" collapse rewrite
 * a list back to empty, which reads as unrestricted. NotIn is honoured in every evaluator, with
 * or without an In entry beside it.
 *
 * An ALLOW never materializes an allow-list where there was none. A field with no id filter
 * already permits every record; turning that into a list naming one would silently revoke
 * every other record, and would freeze out any resource created later.
 */
export function applyAllowance(
  filterOptions: ITypeFieldFilterOptions[] | undefined,
  recordId: string,
  shouldAllow: boolean,
): ITypeFieldFilterOptions[] | undefined {
  const current = filterOptions ?? []

  // Folded into one set per operator: `getAllowedFilterValues` reads only the first matching
  // entry while the evaluator ANDs them all, so duplicates make the tick state and the picker
  // disagree. They are written back as a single entry.
  const collect = (operator: OperatorType): Set<string> => {
    const values = new Set<string>()
    current.forEach(option => {
      if (!isStaticIdFilter(option) || option.operator !== operator) return
      ;(option.values ?? []).forEach(value => value && values.add(value))
    })
    return values
  }

  const allowed = collect(OperatorType.In)
  const denied = collect(OperatorType.NotIn)

  const allowedBefore = new Set(allowed)
  const deniedBefore = new Set(denied)

  if (shouldAllow) {
    denied.delete(recordId)
    if (allowed.size) allowed.add(recordId)
  } else {
    allowed.delete(recordId)
    denied.add(recordId)
  }

  // Hand back the caller's OWN value when this field's answer does not move, so callers can
  // tell by reference whether anything changed. Without it a bulk edit would rewrite - and so
  // mint a new version of - every workflow type it merely looked at.
  //
  // `filterOptions`, not `current`: a field with no restriction at all is the common case,
  // and `current` is a fresh [] for it. Returning that would read as a change against the
  // undefined it came from - defeating the check on exactly the fields it exists to skip -
  // and would persist an empty filterOptions where the document had none.
  const isUnchanged = (before: Set<string>, after: Set<string>): boolean =>
    before.size === after.size && [...before].every(value => after.has(value))

  if (
    isUnchanged(allowedBefore, allowed) &&
    isUnchanged(deniedBefore, denied)
  ) {
    return filterOptions
  }

  const valuesFor = (operator: OperatorType): Set<string> =>
    operator === OperatorType.In ? allowed : denied

  const next: ITypeFieldFilterOptions[] = []
  const rewritten = new Set<OperatorType>()

  current.forEach(option => {
    const operator = option?.operator
    const isRewritable =
      isStaticIdFilter(option) &&
      (operator === OperatorType.In || operator === OperatorType.NotIn)

    if (!isRewritable) {
      next.push(option)
      return
    }
    // Later duplicates were folded into the first, so they are dropped here.
    if (rewritten.has(operator)) return

    rewritten.add(operator)
    const values = valuesFor(operator)
    if (values.size) next.push(staticIdFilter(operator, values))
  })

  // An operator the field did not carry before, now needed - a first deny, or a first allow
  // appended to a list that had been emptied by the edit above.
  ;[OperatorType.In, OperatorType.NotIn].forEach(operator => {
    const values = valuesFor(operator)
    if (rewritten.has(operator) || !values.size) return

    next.push(staticIdFilter(operator, values))
  })

  return next
}

const mapKeepingIdentity = <T>(items: T[] | undefined, map: (item: T) => T) => {
  if (!items) return items
  const next = items.map(map)
  return next.some((item, index) => item !== items[index]) ? next : items
}

// The first static list of an operator, as the server reads them.
const firstStaticList = (
  field: IPermitTypeField | undefined,
  operator: OperatorType,
): ITypeFieldFilterOptions | undefined =>
  field?.filterOptions?.find(
    option => isStaticIdFilter(option) && option.operator === operator,
  )

// Extends the field's existing deny-list with the ids its allow-list does not name; a field
// without a deny-list is left alone, none is created.
const addToDenyList = (
  field: IPermitTypeField,
  recordIds: readonly string[],
): IPermitTypeField => {
  const denyList = firstStaticList(field, OperatorType.NotIn)
  if (!denyList) return field

  // A record the field's allow-list names was allowed there on purpose.
  const allowed = new Set(firstStaticList(field, OperatorType.In)?.values ?? [])
  const values = new Set(denyList.values ?? [])
  const added = recordIds.filter(id => !allowed.has(id) && !values.has(id))
  if (!added.length) return field

  added.forEach(id => values.add(id))
  return {
    ...field,
    filterOptions: field.filterOptions.map(option =>
      option === denyList ? staticIdFilter(OperatorType.NotIn, values) : option,
    ),
  }
}

// Applies `update` to every step field and conditional value, copying only what changes.
const mapFields = (
  workflowSteps: IWorkflowStep[] | undefined,
  update: (field: IPermitTypeField) => IPermitTypeField,
): IWorkflowStep[] | undefined =>
  mapKeepingIdentity(workflowSteps, step => {
    if (!step) return step

    const fields = mapKeepingIdentity(step.fields, update)
    const conditionalFields = mapKeepingIdentity(
      step.conditionalFields,
      conditional => {
        const values = mapKeepingIdentity(conditional?.values, update)
        return values === conditional?.values
          ? conditional
          : { ...conditional, values }
      },
    )

    return fields === step.fields &&
      conditionalFields === step.conditionalFields
      ? step
      : { ...step, fields, conditionalFields }
  })

const fieldsById = (
  workflowSteps: IWorkflowStep[] | undefined,
): Map<string, IPermitTypeField> => {
  const byId = new Map<string, IPermitTypeField>()
  ;(workflowSteps ?? []).forEach(step => {
    ;[
      ...(step?.fields ?? []),
      ...(step?.conditionalFields ?? []).flatMap(c => c?.values ?? []),
    ].forEach(field => field?.id && byId.set(String(field.id), field))
  })
  return byId
}

/**
 * The server's denial for resources turned into workflow resources
 * (PermitTypeNewResourceDenial), applied to a local copy of a permit type: each id joins the
 * deny-list of every field of its resource type that has one, unless that field's allow-list
 * names it. A whole-type save from a copy read before the WR flip has to carry it, or the save
 * drops what the server just wrote.
 *
 * Returns the caller's own steps when nothing moves, and otherwise copies only the steps and
 * fields that changed, so reference checks downstream still see what was touched.
 */
export function denyOnRestrictedFields(
  workflowSteps: IWorkflowStep[] | undefined,
  resourceTypeId: string,
  recordIds: readonly string[],
): IWorkflowStep[] | undefined {
  if (!recordIds.length) return workflowSteps

  return mapFields(workflowSteps, field =>
    field && getResourceTypeIdForFieldType(field.type) === resourceTypeId
      ? addToDenyList(field, recordIds)
      : field,
  )
}

/**
 * Carries into a draft the deny-list entries the server added to the same permit type since
 * the draft's base was read - ids in a field's deny-list on `refreshedSteps` that were not on
 * `baseSteps` - so saving the draft does not drop them. Only additions are carried, so an id
 * the user removed from a deny-list in the draft stays removed. Same rules as
 * denyOnRestrictedFields: only an existing deny-list is extended, and not with an id the
 * field's allow-list names. Identity-preserving like it.
 */
export function carryServerDenials(
  draftSteps: IWorkflowStep[] | undefined,
  baseSteps: IWorkflowStep[] | undefined,
  refreshedSteps: IWorkflowStep[] | undefined,
): IWorkflowStep[] | undefined {
  const base = fieldsById(baseSteps)
  const addedById = new Map<string, string[]>()

  fieldsById(refreshedSteps).forEach((field, id) => {
    const before = new Set(
      firstStaticList(base.get(id), OperatorType.NotIn)?.values ?? [],
    )
    const added = (
      firstStaticList(field, OperatorType.NotIn)?.values ?? []
    ).filter(value => value && !before.has(value))
    if (added.length) addedById.set(id, added)
  })

  if (!addedById.size) return draftSteps

  return mapFields(draftSteps, field => {
    const added = field?.id && addedById.get(String(field.id))
    return added ? addToDenyList(field, added) : field
  })
}
